import React, { useState, useEffect } from 'react';
import { getRounds, generateRound, getGames } from '../services/api';

function AdminGenerateGames({ showFeedback, activeTournament }) {
    const [rounds, setRounds] = useState([]);
    const [games,  setGames]  = useState([]);
    const [busy,   setBusy]   = useState(false);

    const loadGames = () => {
        if (!activeTournament) return;
        getGames(activeTournament.id, null).then(res => setGames(res.data));
    };

    useEffect(() => {
        if (!activeTournament) return;
        getRounds(activeTournament.id).then(res => setRounds(res.data));
        loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTournament]);

    const gamesPerRound = rounds.reduce((acc, r) => {
        acc[r.round_number] = games.filter(g => g.round_number === r.round_number);
        return acc;
    }, {});

    // A tournament is fully initialized when every configured round has at
    // least one game row. Partially initialized means some rounds have games
    // but others don't — this is the half-built state caused by running
    // Initialize before all rounds were created.
    const roundsWithGames = rounds.filter(r => (gamesPerRound[r.round_number] || []).length > 0).length;
    const fullyInitialized   = rounds.length > 0 && roundsWithGames === rounds.length;
    const partiallyInitialized = roundsWithGames > 0 && !fullyInitialized;

    const handleInitialize = async () => {
        setBusy(true);
        try {
            const res = await generateRound(activeTournament.id, 1);
            showFeedback(`Initialized tournament — ${res.data.games_created} games created`);
            loadGames();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to initialize tournament', 'error');
        } finally {
            setBusy(false);
        }
    };

    const getRoundStatus = (roundNumber) => {
        const roundGames = gamesPerRound[roundNumber] || [];
        if (roundGames.length === 0) return 'empty';
        if (roundGames.every(g => g.status === 'final')) return 'complete';
        if (roundGames.some(g => g.status === 'final')) return 'in_progress';
        return 'scheduled';
    };

    if (!activeTournament) return <p className="text-gray-500 text-sm">No tournament selected.</p>;

    return (
        <div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-black text-white mb-2">How this works</h3>
                <p className="text-gray-400 text-sm">
                    This is a one-time setup step. After all 64 teams are seeded, click <strong>Initialize tournament</strong>.
                    The system will create round 1 games from your seeds (1v16, 2v15, etc.) and placeholder games for all later rounds.
                    Later rounds get their teams populated automatically as you record results.
                </p>
                <p className="text-gray-500 text-xs mt-2">
                    All rounds must be configured before initializing. There is no per-round generation — once initialized, the entire tournament structure exists.
                </p>
            </div>

            {partiallyInitialized && (
                <div className="bg-yellow-950 border border-yellow-700 rounded-xl p-5 mb-6">
                    <p className="text-yellow-400 font-bold text-sm mb-1">⚠ Incomplete initialization detected</p>
                    <p className="text-yellow-200 text-sm">
                        Some rounds have games but others don't. This usually means Initialize was run before
                        all rounds were created. To fix this, ensure all {rounds.length} rounds are configured,
                        then click <strong>Initialize tournament</strong> below — the backend will fill in any
                        missing rounds.
                    </p>
                </div>
            )}

            {!fullyInitialized && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6 text-center">
                    {!partiallyInitialized && (
                        <p className="text-gray-400 text-sm mb-4">No games have been created yet for this tournament.</p>
                    )}
                    <button onClick={handleInitialize} disabled={busy}
                        className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-300 text-white font-bold px-6 py-3 rounded-lg transition-colors">
                        {busy ? 'Initializing…' : 'Initialize tournament'}
                    </button>
                </div>
            )}

            {roundsWithGames > 0 && (
                <div className="space-y-3">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Round status</p>
                    {rounds.map(r => {
                        const status      = getRoundStatus(r.round_number);
                        const roundGames  = gamesPerRound[r.round_number] || [];
                        const finalCount  = roundGames.filter(g => g.status === 'final').length;
                        const realTeams   = roundGames.filter(g => g.team1_id && g.team2_id).length;

                        return (
                            <div key={r.id} className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-4 flex items-center justify-between">
                                <div>
                                    <p className="text-white font-bold text-sm">{r.round_name}</p>
                                    <p className="text-gray-500 text-xs">
                                        {status === 'complete'    ? `${roundGames.length} games — all final` :
                                         status === 'in_progress' ? `${finalCount} of ${roundGames.length} games final` :
                                         status === 'scheduled' && r.round_number === 1 ? `${roundGames.length} games scheduled` :
                                         status === 'scheduled' ? `${realTeams} of ${roundGames.length} games have teams; ${roundGames.length - realTeams} awaiting prior results` :
                                         'No games'}
                                    </p>
                                </div>
                                <span className={'text-xs px-2 py-0.5 rounded-full ' +
                                    (status === 'complete'    ? 'bg-green-900 text-green-400' :
                                     status === 'in_progress' ? 'bg-yellow-900 text-yellow-400' :
                                     status === 'scheduled'   ? 'bg-blue-900 text-blue-400' :
                                     'bg-gray-800 text-gray-500')}>
                                    {status === 'complete'    ? 'Complete' :
                                     status === 'in_progress' ? 'In progress' :
                                     status === 'scheduled'   ? 'Scheduled' : '—'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default AdminGenerateGames;